import { createSlice } from '@reduxjs/toolkit';
import SearchUseCase from './searchUseCase';

// Initial state for search results
const initialState = {
  searchResults: [],
  resultCount: 0,
  requestStatus: '',
  error: '',
  isRequestInProgress: false,
  profilePhotoCollection: [],
  suggestionList: [],
  refiners: [],
  latency: 0,
  homePageSearchFlag: false,
  flagForSuggestedOrRecentSearch: '',
  previewToken: '',
  bestBetsResults: [],
  bestBetsFlag: false,
  isBestBetRequestInProgress: false,
  previewStats: [],
  nestedRefiners: [],
  selectedRefiners: [],
  selectedRefinersOnPageLoad: [],
  refinersClicked: [],
  ontologyData: [],
  flagForContentAge: false,
  flagForClear: false,
  insightResults: [],
  insightFlag: false,
  insightsLeads: [],
  insightsLeadsPhotos: [],
  isSemanticSearchEnabled: false,
  searchMode: 'Keyword' as 'Keyword' | 'Semantic',
  isLoadingPreferences: true,
  impersonationInfo: null as {
    userName: string | null;
    previewMailValid: boolean | null;
    isDataImpersonated: boolean | null;
  } | null
};

/**
 * search reducer with search api action type and set the state
 * reducer is part of redux and here extraReducers method is used to
 * set dynamic case for async functions actions. once we receive the
 * fullfil action, we will set the value to state passed as async action method response
 *
 */
const searchReducer = createSlice({
  name: 'search',
  initialState,
  reducers: {
    // resetting the search results to initial state i.e., empty
    resetSearchResults() {
      return initialState;
    },
    // Setting up a flag value to identify the search initiated from Home page
    setHomePageSearchFlag(state, action) {
      state.homePageSearchFlag = action.payload;
    },
    // Setting up the flag value for either Recent Search Click or Suggestions Click
    enableFlagForSuggestedOrRecentSearch(state, action) {
      state.flagForSuggestedOrRecentSearch = action.payload;
    },
    // To set preview token in Redux
    setPreviewToken(state, action) {
      state.previewToken = action.payload;
    },
    // to set best bets flag
    setBestBetFlag(state, action) {
      state.bestBetsFlag = action.payload;
    },
    // to set insights flag
    setInsightsFlag(state, action) {
      state.insightFlag = action.payload;
    },
    // to set insights results
    setInsightsResults(state, action) {
      state.insightResults = action.payload;
    },
    // to set the selected refiners values on refiner apply button click
    setSelectedRefiners(state, action) {
      state.selectedRefiners = action.payload;
    },
    // to set the selected refiners on page load
    setSelectedRefinersOnPageLoad(state, action) {
      state.selectedRefinersOnPageLoad = action.payload;
    },
    // to set the  refiners clicked before apply
    setRefinersClicked(state, action) {
      state.refinersClicked = action.payload;
    },
    // to set if the contentAge clicked before apply
     setFlagForContentAge(state, action) {
      state.flagForContentAge = action.payload;
    },

    // to check if the clear is clicked 
        setFlagForClear(state, action) {
          state.flagForClear = action.payload;
        },
    // Set toggle type for keyword/semantic search
    setSearchMode(state, action) {
      state.searchMode = action.payload;
    },
    // Set loading preferences flag
    setIsLoadingPreferences(state, action) {
      state.isLoadingPreferences = action.payload;
    },
    // Clear insights leads and photos
    clearInsightsLeads(state) {
      state.insightsLeads = [];
      state.insightsLeadsPhotos = [];
    }
  },
  extraReducers: (builder) => {
    // builder.addCase(@param1:async function promise, @param2: call back handler function)
    builder.addCase(SearchUseCase.doSearch.fulfilled, (state, action) => {
      state.searchResults = action.payload.results;
      state.resultCount = action.payload.resultCount;
      state.isSemanticSearchEnabled = action.payload.isSemanticSearchEnabled ?? false;
      state.requestStatus = action.meta.requestStatus
        ? action.meta.requestStatus
        : '';
      state.error = '';
      state.isRequestInProgress = false;
      state.refiners = action.payload.refiners;
      state.latency = action.payload.latency;
      state.nestedRefiners = action.payload.nestedRefiners;
      state.impersonationInfo = action.payload.impersonationInfo ?? null;
    });

    // added case to handle rejected scenario.
    builder.addCase(SearchUseCase.doSearch.rejected, (state, action) => {
      state.error = action.error.message ? action.error.message : '';
      state.requestStatus = action.meta.requestStatus
        ? action.meta.requestStatus
        : '';
      state.resultCount = 0;
      state.searchResults = [];
      state.isRequestInProgress = false;
      state.refiners = [];
      state.latency = 0;
      state.nestedRefiners = [];
      state.impersonationInfo = null;
    });

    builder.addCase(SearchUseCase.doSearch.pending, (state, _action) => {
      state.resultCount = 0;
      state.searchResults = [];
      state.isRequestInProgress = true;
      state.requestStatus = '';
      state.refiners = [];
      state.latency = 0;
      state.nestedRefiners = [];
      state.impersonationInfo = null;
    });

    //case for people graph api call fullfill
    builder.addCase(
      SearchUseCase.getUserProfilePic.fulfilled,
      (state, action) => {
        let pplImages: any = [];
        for (const pplImagePayload of action.payload) {
          pplImages.push(...pplImagePayload.responses);
        }
        state.profilePhotoCollection = pplImages;
      }
    );
    //case for people graph api call pending, restting profile pic collection.
    builder.addCase(
      SearchUseCase.getUserProfilePic.pending,
      (state, _action) => {
        state.profilePhotoCollection = [];
      }
    );

    //case for leads data graph api call fulfilled
    builder.addCase(
      SearchUseCase.getInsightLeadsFromGraph.fulfilled,
      (state, action) => {
        let leads: any = [];
        for (const lead of action.payload) {
          leads.push(...lead.responses);
        }
        state.insightsLeads = leads;
      }
    );
    //case for leads data graph api call pending
    builder.addCase(
      SearchUseCase.getInsightLeadsFromGraph.pending,
      (state, _action) => {
        state.insightsLeads = [];
      }
    );

    //case for leads photos graph api call fulfilled
    builder.addCase(
      SearchUseCase.getInsightLeadsPhotosFromGraph.fulfilled,
      (state, action) => {
        let leadsPhotos: any = [];
        for (const lead of action.payload) {
          leadsPhotos.push(...lead.responses);
        }
        state.insightsLeadsPhotos = leadsPhotos;
      }
    );
    //case for leads photos graph api call pending
    builder.addCase(
      SearchUseCase.getInsightLeadsPhotosFromGraph.pending,
      (state, _action) => {
        state.insightsLeadsPhotos = [];
      }
    );

    // Case for Suggestions BAI call pending.
    builder.addCase(
      SearchUseCase.queryRecommondations.pending,
      (state, action) => {
        state.suggestionList = [];
      }
    );

    // Case for Suggestions BAI call fulfilled.
    builder.addCase(
      SearchUseCase.queryRecommondations.fulfilled,
      (state, action) => {
        state.suggestionList = action.payload.suggestionList;
      }
    );
 
 //Case for Ontology API call pending.
     builder.addCase(
      SearchUseCase.fetchOntologyData.pending,
      (state, action) => {
        state.ontologyData = [];
      }
     );
   
//Case for Ontology API call fulfilled.
     builder.addCase(
      SearchUseCase.fetchOntologyData.fulfilled,
      (state, action) => {
        let objectLabelName: any = [];
        for (const objectLabel of action.payload.ontologyList) {
          if (objectLabel !== "") {
            objectLabelName.push(objectLabel);
          }
        }
        state.ontologyData = objectLabelName;
      }
    );
    // Case for bestbets results  call fulfilled.
    builder.addCase(SearchUseCase.bestbets.fulfilled, (state, action) => {
      state.bestBetsResults = action.payload.bestBetsResults;
      state.isBestBetRequestInProgress = false;
    });

    // Case for bestbets results  call pending.
    builder.addCase(SearchUseCase.bestbets.pending, (state, action) => {
      state.bestBetsResults = [];
      state.isBestBetRequestInProgress = true;
    });

    // Case for bestbets results  call rejected.
    builder.addCase(SearchUseCase.bestbets.rejected, (state, action) => {
      state.bestBetsResults = [];
      state.isBestBetRequestInProgress = false;
    });

    // Case for insights results  call fulfilled.
    builder.addCase(SearchUseCase.insights.fulfilled, (state, action) => {
      state.insightResults = action.payload.insightResults;
    });

    // Case for insights results  call pending.
    builder.addCase(SearchUseCase.insights.pending, (state, action) => {
      state.insightResults = [];
    });

    // Case for bestbets results  call rejected.
    builder.addCase(SearchUseCase.insights.rejected, (state, action) => {
      state.insightResults = [];
    });

    // Case for Preview Stats call fulfilled.
    builder.addCase(
      SearchUseCase.getPreviewStats.fulfilled,
      (state, action) => {
        state.previewStats = action.payload.previewStats;
      }
    );

    // Case for Preview Stats call pending.
    builder.addCase(SearchUseCase.getPreviewStats.pending, (state, action) => {
      state.previewStats = [];
    });

    // Case for Preview Stats call rejected.
    builder.addCase(SearchUseCase.getPreviewStats.rejected, (state, action) => {
      state.previewStats = [];
    });

    // Case for fetchUserPreferences fulfilled
    builder.addCase(SearchUseCase.fetchUserPreferences.fulfilled, (state, action) => {
      state.searchMode = action.payload.searchMode;
      state.isLoadingPreferences = false;
    });

    // Case for fetchUserPreferences rejected
    builder.addCase(SearchUseCase.fetchUserPreferences.rejected, (state, action) => {
      state.searchMode = 'Keyword';
      state.isLoadingPreferences = false;
    });

    // Case for fetchUserPreferences pending
    builder.addCase(SearchUseCase.fetchUserPreferences.pending, (state, _action) => {
      state.isLoadingPreferences = true;
    });
  }
});

export const {
  setPreviewToken,
  resetSearchResults,
  setHomePageSearchFlag,
  enableFlagForSuggestedOrRecentSearch,
  setBestBetFlag,
  setSelectedRefiners,
  setSelectedRefinersOnPageLoad,
  setRefinersClicked,
  setFlagForContentAge,
  setFlagForClear,
  setInsightsFlag,
  setInsightsResults,
  setSearchMode,
  setIsLoadingPreferences,
  clearInsightsLeads
} = searchReducer.actions;
export default searchReducer.reducer;
